import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { CalendarArrowIcon } from "@whoosh/design-system/src/components/icons_v2/general/icons/CalendarArrowIcon";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { ClubStackNavigatorParamList } from "../../../../../../../navigators/ClubNavigator";
import { useShouldPresentBookingAsLockedRequest } from "../BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { MoveBookingButton_Booking$key } from "./__generated__/MoveBookingButton_Booking.graphql";

type Props = {
  bookingRef: MoveBookingButton_Booking$key | null;
  currentSessionUserClientId: string;
};
export const MoveBookingButton = ({
  bookingRef,
  currentSessionUserClientId,
}: Props) => {
  const booking = useFragment(MoveBookingButtonFragmentString, bookingRef);
  const canUpdate = booking?.canUpdate;
  const isGolfFacility =
    booking?.startSlot?.agenda?.facility?.type === "GOLF_COURSE";

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const isRequestBooking = booking?.startSlot?.__typename === "QueueSlot";
  const shouldPresentAsRequest = useShouldPresentBookingAsLockedRequest(
    booking ?? null
  );

  const bookingIsInCancelWindow = booking?.startSlot?.isInCancelWindow;
  const userIsBookingOwner =
    booking?.ownerClient?.id === currentSessionUserClientId;

  const requiresPrePayment =
    booking?.paymentRequirements?.prePaymentRequirement === "FULL";
  const bookingLockedDueToPrePaymentAndStaffCreated =
    booking?.source === "STAFF" && requiresPrePayment;

  const canMoveBooking =
    canUpdate &&
    !bookingIsInCancelWindow &&
    userIsBookingOwner &&
    booking?.status === "CONFIRMED" &&
    !bookingLockedDueToPrePaymentAndStaffCreated &&
    isGolfFacility &&
    !isRequestBooking &&
    !shouldPresentAsRequest;

  const { showToast } = useToast();

  const handleMoveBookingPress = () => {
    if (booking) {
      navigation.navigate("MoveBooking", {
        originalBookingId: booking?.id ?? "",
        originalFacilityId: booking?.startSlot?.agenda?.facility?.id ?? "",
        date: booking?.startSlot?.agenda.date ?? "",
        originalSlotId: booking?.startSlot?.id ?? "",
        facilitySlug: booking?.startSlot?.agenda?.facility?.slug ?? "",
        bookingType: booking?.type || "STANDARD",
      });
    } else {
      showToast({
        description: t`There was an error moving the booking. Please try again.`,
        semanticStyle: "error",
        toastId: "moveBookingError",
        testID: "move-booking-error-toast",
      });
    }
  };

  return !canMoveBooking ? null : (
    <Button
      accessibilityLabel={t`Move Booking button`}
      size="small"
      variant="secondary"
      onPress={handleMoveBookingPress}
      leftIcon={<CalendarArrowIcon color="icon-primary" />}
    >
      {t`Move Booking`}
    </Button>
  );
};

const MoveBookingButtonFragmentString = graphql`
  fragment MoveBookingButton_Booking on Booking {
    paymentRequirements {
      prePaymentRequirement
    }
    id
    source
    type
    ...useShouldPresentBookingAsLockedRequest_booking
    ... on StandardBooking {
      status
    }
    ownerClient {
      ... on Member {
        id
      }
      ... on Customer {
        id
      }
    }
    startSlot {
      __typename
      ... on TimeSlot {
        isInCancelWindow
      }
      id
      agenda {
        facility {
          type
          id
          slug
        }
      }
      ... on TimeSlot {
        time
        course {
          id
        }
      }
      agenda {
        date
      }
    }
    canUpdate
  }
`;
